// Shared page renderer; family entry points own queries, models and Head exports.
import React from 'react';
import Layout from '../global/layout';
import ReferenceComponent from '../components/ReferenceComponent';
import TaxonomyProvider from './TaxonomyContext';
import NavigationComponent from '../components/NavigationComponent';
import PageMetadataProvider from './PageMetaDataContext';
import GeoRegionBanner from '../components/GeoRegionBanner';
import { navigate, Slice } from 'gatsby';
import sharedPagePaths from './sharedPagePaths';
import { PersistentPageContext } from './PersistentPageContext';
import Cookies from 'js-cookie';
import { normalizeLocale } from '../utils/locale';
import { SmartLinkProvider } from '../contexts/SmartLinkContext';
import { ReferralCodeProvider } from '../contexts/ReferralCodeContext';
import { GeoRegionProvider } from '../contexts/GeoRegionContext';
import { PageDatasetsContext } from './PageDatasetsContext';

import type { PageTemplateProps } from './models';
import { getColor } from '../components/Global/DataUtils';

export const PageShell: React.FC<
    PageTemplateProps & { children?: React.ReactNode }
> = ({ data, pageContext, location, children, ...props }) => {
    const isUK =
        data?.kontentItemLayoutMasterPage?.preferred_language?.toLowerCase() ===
        'gb-en';
    React.useEffect(() => {
        const searchParam =
            typeof window === 'undefined' ? '' : window.location.search;
        const urlParams = new URLSearchParams(searchParam);
        const isValidParam = (v: string | null): v is string =>
            !!v && v !== 'null' && v !== 'undefined';
        const promoCode = urlParams.get('mbsy');
        const gclid = urlParams.get('gclid');
        const gbraid = urlParams.get('gbraid');
        const wbraid = urlParams.get('wbraid');
        const msclkid = urlParams.get('msclkid');
        const fbclid = urlParams.get('fbclid');
        if (isUK) {
            const imRef = urlParams.get('im_ref');
            if (isValidParam(imRef)) {
                Cookies.set('im_ref', imRef, { path: '/', expires: 90 });
            }
        }
        if (isValidParam(promoCode)) {
            Cookies.set('promocode', promoCode, { path: '/', expires: 90 });
        }
        if (isValidParam(gclid)) {
            Cookies.set('gclid', gclid, { path: '/', expires: 90 });
        }
        if (isValidParam(gbraid)) {
            Cookies.set('gbraid', gbraid, { path: '/', expires: 90 });
        }
        if (isValidParam(wbraid)) {
            Cookies.set('wbraid', wbraid, { path: '/', expires: 90 });
        }
        if (isValidParam(msclkid)) {
            Cookies.set('msclkid', msclkid, { path: '/', expires: 90 });
        }
        if (isValidParam(fbclid)) {
            Cookies.set('fbclid', fbclid, { path: '/', expires: 28 });
        }
        if (searchParam.includes('?email=') && searchParam.includes('%40')) {
            const updatedSearchParam = searchParam.replace('%40', '@');
            const redirectUrl = `${window.location.pathname}${updatedSearchParam}`;
            navigate(redirectUrl, { replace: true });
        }
    }, [isUK, location.search]);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;

        window.lockABTastyTag = true;

        if (typeof window.unlockABTasty === 'function') {
            window.unlockABTasty();
            window.lockABTastyTag = false;
            return;
        }

        const scheduleObserver = () => {
            const observer = new MutationObserver(() => {
                if (typeof window.unlockABTasty === 'function') {
                    window.unlockABTasty();
                    window.lockABTastyTag = false;
                    observer.disconnect();
                }
            });
            observer.observe(document.documentElement, {
                childList: true,
                subtree: true,
            });

            const fallback = setTimeout(() => {
                if (typeof window.unlockABTasty === 'function') {
                    window.unlockABTasty();
                    window.lockABTastyTag = false;
                }
                observer.disconnect();
            }, 3000);

            return { observer, fallback };
        };

        let cleanup: {
            observer: MutationObserver;
            fallback: ReturnType<typeof setTimeout>;
        } | null = null;
        const idle = (
            typeof requestIdleCallback === 'function'
                ? requestIdleCallback
                : setTimeout
        )(() => {
            cleanup = scheduleObserver();
        });

        return () => {
            (typeof cancelIdleCallback === 'function'
                ? cancelIdleCallback
                : clearTimeout)(idle as any);
            if (cleanup) {
                cleanup.observer.disconnect();
                clearTimeout(cleanup.fallback);
            }
        };
    }, []);

    const elements = data?.kontentItemLayoutMasterPage?.elements;
    const blogMetadata =
        elements.other_metadata?.value[0]?.elements || undefined;
    const isBlogArticle = Boolean(blogMetadata);
    const blogPublicationString = blogMetadata
        ? elements.page_components?.value?.filter(
              (component) =>
                  component?.__typename ===
                  'kontent_item_component___blog_masthead'
          )[0]?.elements?.display_publish_date?.value
        : null;
    const blogTitle = blogMetadata ? blogMetadata?.title?.value : '';
    const blogShortDescription = blogMetadata
        ? blogMetadata?.short_description?.value
        : '';
    const displayPageName = elements?.display_page_name?.value || '';
    const pageBackgroundColor =
        getColor(elements?.page_background_color) || undefined;

    const preferredLanguageRaw =
        data?.kontentItemLayoutMasterPage?.preferred_language;
    const preferredLanguage = normalizeLocale(
        preferredLanguageRaw || 'default'
    );

    const pageMetadata = React.useMemo(
        () => ({
            slug: elements.slug.value,
            isBlogArticle,
            blogTitle: blogTitle,
            blogShortDescription: blogShortDescription,
            displayPageName: displayPageName,
            preferredLanguage: preferredLanguage,
            location: location,
            pagePaths: pageContext?.paths || sharedPagePaths,
            availableCountries: pageContext?.availableCountries || [],
        }),
        [
            isBlogArticle,
            blogTitle,
            blogShortDescription,
            displayPageName,
            elements.slug.value,
            preferredLanguage,
            location,
            pageContext?.paths,
            pageContext?.availableCountries,
        ]
    );

    const pageDatasets = React.useMemo(
        () => ({
            ...data,
            blogScrollSelections: pageContext?.blogScrollSelections,
        }),
        [data, pageContext?.blogScrollSelections]
    );

    const sliceMetadata = {
        slug: elements.slug.value,
        preferredLanguage,
        location: {
            pathname: location.pathname,
            search: location.search || '',
            hash: location.hash || '',
        },
        availableCountries: pageContext?.availableCountries || [],
    };

    return (
        <PageDatasetsContext.Provider value={pageDatasets}>
            <ReferralCodeProvider
                enabled={elements?.slug?.value === 'friend'}
                pathname={location.pathname}
                search={location.search || ''}
            >
                <PageMetadataProvider metadata={pageMetadata}>
                    <TaxonomyProvider
                        wellness_type={elements.wellness_type}
                        blog_topic={elements.blog_topic}
                        for_page_relevancy={elements.for_page_relevancy}
                    >
                        <SmartLinkProvider
                            languageCodename={preferredLanguageRaw || 'default'}
                        >
                            <Layout>
                                <div
                                    style={{
                                        backgroundColor: pageBackgroundColor,
                                    }}
                                    data-hj-ignore-attributes={true}
                                    data-kontent-item-id={
                                        data?.kontentItemLayoutMasterPage
                                            ?.system?.id
                                    }
                                >
                                    <div className="wrapper">
                                        {elements?.navigation_elements?.value?.map(
                                            (component, idx) => {
                                                if (
                                                    component?.system?.type ===
                                                    'component___footer'
                                                )
                                                    return null;
                                                if (
                                                    component?.system?.type ===
                                                    'component___navigation'
                                                )
                                                    return null;
                                                return (
                                                    <NavigationComponent
                                                        name={
                                                            component?.system
                                                                ?.type
                                                        }
                                                        key={`${component?.__typename}${idx}`}
                                                        kontentItemId={
                                                            component?.system
                                                                ?.id
                                                        }
                                                        {...component?.elements}
                                                    />
                                                );
                                            }
                                        )}

                                        <GeoRegionProvider
                                            preferredLanguage={
                                                preferredLanguage
                                            }
                                        >
                                            <GeoRegionBanner />
                                        </GeoRegionProvider>

                                        {pageContext.sharedLayout ? (
                                            <Slice
                                                alias="site-navigation"
                                                metadata={sliceMetadata}
                                            />
                                        ) : (
                                            elements?.navigation_elements?.value?.map(
                                                (component, idx) => {
                                                    if (
                                                        component?.system
                                                            ?.type !==
                                                        'component___navigation'
                                                    )
                                                        return null;
                                                    return (
                                                        <NavigationComponent
                                                            name={
                                                                component
                                                                    ?.system
                                                                    ?.type
                                                            }
                                                            key={`${component?.__typename}${idx}`}
                                                            kontentItemId={
                                                                component
                                                                    ?.system?.id
                                                            }
                                                            {...component?.elements}
                                                        />
                                                    );
                                                }
                                            )
                                        )}
                                        {children || (
                                            <PageContent data={data} />
                                        )}
                                        {pageContext.sharedLayout ? (
                                            <Slice
                                                alias="site-footer"
                                                metadata={sliceMetadata}
                                            />
                                        ) : (
                                            elements?.navigation_elements?.value?.map(
                                                (component, idx) => {
                                                    if (
                                                        component?.system
                                                            ?.type !==
                                                        'component___footer'
                                                    )
                                                        return null;
                                                    return (
                                                        <NavigationComponent
                                                            name={
                                                                component
                                                                    ?.system
                                                                    ?.type
                                                            }
                                                            key={`${component?.__typename}${idx}`}
                                                            kontentItemId={
                                                                component
                                                                    ?.system?.id
                                                            }
                                                            {...component?.elements}
                                                        />
                                                    );
                                                }
                                            )
                                        )}
                                    </div>
                                </div>
                            </Layout>
                        </SmartLinkProvider>
                    </TaxonomyProvider>
                </PageMetadataProvider>
            </ReferralCodeProvider>
        </PageDatasetsContext.Provider>
    );
};

const PageContent = ({ data }: Pick<PageTemplateProps, 'data'>) => {
    const elements = data?.kontentItemLayoutMasterPage?.elements;
    return (
        <div
            data-kontent-add-button="page_components"
            data-kontent-item-id={data?.kontentItemLayoutMasterPage?.system?.id}
        >
            {elements?.page_components?.value?.map((component, idx) => (
                <ReferenceComponent
                    name={component?.system?.type}
                    key={`${component?.__typename}${idx}`}
                    kontentItemId={component?.system?.id}
                    {...component?.elements}
                />
            ))}
        </div>
    );
};

const PageTemplate: React.FC<PageTemplateProps> = (props) => {
    const persistent = React.useContext(PersistentPageContext);
    return persistent ? (
        <PageContent key={props.location.pathname} data={props.data} />
    ) : (
        <PageShell {...props} />
    );
};

export default PageTemplate;
